import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { sampleSuppliers } from '../data/suppliers';
import { useLang } from '../lib/i18n/LanguageContext';
import { dictionary } from '../lib/i18n/dictionary';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ArrowLeftIcon, MapPinIcon, BuildingIcon, CheckIcon, XIcon, GlobeIcon, PhoneCallIcon } from 'lucide-react';
import { useScheduleCall } from '../components/ScheduleCallModal';
function Yes({
  on
}: {
  on: boolean;
}) {
  return on ? <span className="inline-flex items-center gap-1 text-emerald-700 text-sm" data-id="element-799">
      <CheckIcon className="w-3.5 h-3.5" data-id="element-800" /> Yes
    </span> : <span className="inline-flex items-center gap-1 text-slate-400 text-sm" data-id="element-801">
      <XIcon className="w-3.5 h-3.5" data-id="element-802" /> No
    </span>;
}
export function SupplierDetailPage() {
  const {
    id
  } = useParams();
  const {
    lang
  } = useLang();
  const supplier = sampleSuppliers.find(s => s.id === id);
  const t = dictionary.supplierDetail;
  const c = dictionary.common;
  const dt = dictionary.directory;
  const {
    open: openSchedule
  } = useScheduleCall();
  if (!supplier) {
    return <main className="pt-32 pb-24 min-h-screen bg-white" data-id="element-803">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center" data-id="element-804">
          <h1 className="font-serif text-3xl text-navy-deep mb-4" data-id="element-805">
            {t.notFound[lang]}
          </h1>
          <Link to="/suppliers" data-id="element-806">
            <Button variant="primary" data-id="element-807">{c.backToDirectory[lang]}</Button>
          </Link>
        </div>
      </main>;
  }
  return <main className="pt-32 pb-24 min-h-screen bg-white" data-id="element-808">
      <div className="max-w-5xl mx-auto px-5 md:px-8" data-id="element-809">
        <Link to="/suppliers" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-deep transition-colors mb-8" data-id="element-810">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-811" />
          {c.backToDirectory[lang]}
        </Link>

        <div className="border border-slate-200 rounded-md p-7 md:p-10 bg-slate-50/40" data-id="element-812">
          <div className="flex items-start justify-between gap-4 mb-6" data-id="element-813">
            <div className="flex items-center gap-3" data-id="element-814">
              <div className="w-12 h-12 flex items-center justify-center bg-navy-deep/5 border border-navy-deep/10 rounded-sm" data-id="element-815">
                <BuildingIcon className="w-5 h-5 text-navy-deep" data-id="element-816" />
              </div>
              <Badge variant="gold" data-id="element-817">{c.sampleProfile[lang]}</Badge>
            </div>
            <span className="font-mono text-[11px] text-slate-400" data-id="element-818">
              {supplier.id}
            </span>
          </div>

          <h1 className="font-serif text-3xl md:text-4xl text-navy-deep leading-tight" data-id="element-819">
            {supplier.company_name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500" data-id="element-820">
            <span className="inline-flex items-center gap-1.5" data-id="element-821">
              <MapPinIcon className="w-3.5 h-3.5" data-id="element-822" /> {supplier.city},{' '}
              {supplier.state}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-823">
              <GlobeIcon className="w-3.5 h-3.5" data-id="element-824" /> {supplier.website}
            </span>
          </div>

          <div className="mt-4" data-id="element-825">
            <Badge variant={supplier.verification_status === 'Verified' ? 'success' : supplier.verification_status === 'Invite Only' ? 'invite' : 'pending'} data-id="element-826">
              {dt.statuses[supplier.verification_status][lang]}
            </Badge>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5 mt-6" data-id="element-827">
          <div className="lg:col-span-2 space-y-5" data-id="element-828">
            <section className="border border-slate-200 rounded-md p-7 bg-white" data-id="element-829">
              <h2 className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold-deep mb-3" data-id="element-830">
                {t.overview[lang]}
              </h2>
              <h3 className="font-serif text-xl text-navy-deep mb-2" data-id="element-831">
                {t.productsServices[lang]}
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-832">
                {supplier.products_services}
              </p>
            </section>

            <section className="border border-slate-200 rounded-md p-7 bg-white" data-id="element-833">
              <h2 className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold-deep mb-3" data-id="element-834">
                {t.certifications[lang]}
              </h2>
              <div className="flex flex-wrap gap-2" data-id="element-835">
                {supplier.certifications.map(c2 => <Badge key={c2} data-id="element-836">{c2}</Badge>)}
              </div>
            </section>

            <section className="border border-slate-200 rounded-md p-7 bg-white" data-id="element-837">
              <h2 className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold-deep mb-3" data-id="element-838">
                {t.capability[lang]}
              </h2>
              <dl className="grid sm:grid-cols-2 gap-4 text-sm" data-id="element-839">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-id="element-840">
                  <dt className="text-slate-600" data-id="element-841">{t.apiRelated[lang]}</dt>
                  <dd data-id="element-842">
                    <Yes on={supplier.api_related} data-id="element-843" />
                  </dd>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-id="element-844">
                  <dt className="text-slate-600" data-id="element-845">{t.isoCertified[lang]}</dt>
                  <dd data-id="element-846">
                    <Yes on={supplier.iso_certified} data-id="element-847" />
                  </dd>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-id="element-848">
                  <dt className="text-slate-600" data-id="element-849">{t.exportReady[lang]}</dt>
                  <dd data-id="element-850">
                    <Yes on={supplier.export_ready} data-id="element-851" />
                  </dd>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-id="element-852">
                  <dt className="text-slate-600" data-id="element-853">{t.latamExperience[lang]}</dt>
                  <dd data-id="element-854">
                    <Yes on={supplier.latin_america_experience} data-id="element-855" />
                  </dd>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-id="element-856">
                  <dt className="text-slate-600" data-id="element-857">{t.spanishSupport[lang]}</dt>
                  <dd data-id="element-858">
                    <Yes on={supplier.spanish_support} data-id="element-859" />
                  </dd>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-id="element-860">
                  <dt className="text-slate-600" data-id="element-861">
                    {t.relationshipStatus[lang]}
                  </dt>
                  <dd className="text-navy-deep" data-id="element-862">
                    {supplier.relationship_status}
                  </dd>
                </div>
              </dl>
            </section>
          </div>

          <aside className="space-y-5" data-id="element-863">
            <div className="border border-slate-200 rounded-md p-6 bg-white sticky top-24" data-id="element-864">
              <p className="text-sm text-slate-600 leading-relaxed mb-5" data-id="element-865">
                {t.requestNote[lang]}
              </p>
              <div className="space-y-2.5" data-id="element-866">
                <a href="/#buyer-form" className="block" data-id="element-867">
                  <Button variant="gold" className="w-full" data-id="element-868">
                    {c.requestIntroduction[lang]}
                  </Button>
                </a>
                <Button variant="secondary" className="w-full !text-navy-deep !border-slate-300 hover:!bg-slate-50" onClick={openSchedule} data-id="element-869">
                  <PhoneCallIcon className="w-4 h-4" data-id="element-870" />
                  {c.scheduleCall[lang]}
                </Button>
              </div>
            </div>

            <div className="border border-slate-200 rounded-md p-6 bg-white" data-id="element-871">
              <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-slate-400 mb-2" data-id="element-872">
                {t.sourceDirectory[lang]}
              </p>
              <p className="text-sm text-navy-deep" data-id="element-873">
                {supplier.source_directory}
              </p>
              <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-slate-400 mb-2 mt-5" data-id="element-874">
                {t.category[lang]}
              </p>
              <p className="text-sm text-navy-deep" data-id="element-875">{supplier.category}</p>
              {supplier.sub_category && <>
                  <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-slate-400 mb-2 mt-5" data-id="element-876">
                    {t.subCategory[lang]}
                  </p>
                  <p className="text-sm text-slate-600" data-id="element-877">
                    {supplier.sub_category}
                  </p>
                </>}
            </div>
          </aside>
        </div>
      </div>
    </main>;
}